import { useContext, useState } from "react";
import AuthContext from "../contexts/AuthContext";
import SnackbarContext from "../contexts/SnackbarContext";

export interface ILoader {
    loading: boolean;
    error: boolean;
    errorMessage: string;
    successMessage?: string;
}

export const defaultLoader: ILoader = {
    loading: false,
    error: false,
    errorMessage: "",
}

export interface FetchCallResponse<T> {
    status: number;
    ok: boolean;
    data?: T;
    parsed: boolean;
    errorMessage?: string;
    headers?: Headers;
}

export default function useFetch(loadByDefault: boolean = false, isFile: boolean = false) {
    const { user, getToken } = useContext(AuthContext);
    const [loader, setLoader] = useState<ILoader>({
        ...defaultLoader,
        loading: loadByDefault
    });
    const snackbar = useContext(SnackbarContext);

    async function call<T, Q>(method: string, apiRoute: string, body?: T): Promise<FetchCallResponse<Q>> {
        if (user == null) {
            setLoader({
                loading: false,
                error: true,
                errorMessage: "User is not logged in",
            });
            return {
                status: 401,
                ok: false,
                parsed: false
            }
        }
        setLoader({ ...loader, loading: true, error: false });
        const token = await getToken();
        if (token == null) {
            throw new Error("Firebase authentication error.");
        }
        const options = isFile ? constructFileUploadFetchOptions(method, token, body) : constructFetchOptions(method, token, body);
        const response = await fetch(apiRoute, options as RequestInit);
        if (response.ok) {
            try {
                const data = isFile ? await response.blob() : await response.json();
                setLoader({
                    loading: false,
                    error: false,
                    errorMessage: "",
                });
                return {
                    status: response.status,
                    ok: response.ok,
                    data,
                    parsed: true,
                    headers: response.headers,
                };
            } catch (e) {
                setLoader({
                    loading: false,
                    error: true,
                    errorMessage: "Invalid response from server",
                });
                return {
                    status: response.status,
                    ok: response.ok,
                    parsed: false
                };
            }
        } else {
            const errorMessage = await constructErrorMessage(response);
            snackbar.showMessage(errorMessage, "error");
            setLoader({
                loading: false,
                error: true,
                errorMessage: errorMessage,
            });
            return {
                status: response.status,
                ok: response.ok,
                parsed: false,
                errorMessage: errorMessage,
            };
        }
    }

    return {
        ...loader,
        call
    }
}

export function constructFetchOptions(method: string, idToken: string, data?: any) {
    const options = {
        method: method,
        cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
            "Accept": "application/json",
            'Content-Type':  'application/json',
            "Authorization": `Bearer ${idToken}`
        },
        redirect: 'follow', // manual, *follow, error
        referrerPolicy: 'no-referrer'
    }
    if (method != "GET" && data != null) {
        (options as any)["body"] = JSON.stringify(data);
    }
    return options;
}

function constructFileUploadFetchOptions(method: string, idToken: string, data?: any) {
    const options = {
        method: method,
        cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        redirect: 'follow', // manual, *follow, error
        referrerPolicy: 'no-referrer',
        headers: {
            "Authorization": `Bearer ${idToken}`
        },
        body: data
    }
    return options;
}

export async function constructErrorMessage(response: Response) {
    try {
        const errorBody = await response.json();
        if (typeof errorBody === "string") return errorBody;
        if (typeof errorBody === "object" && errorBody !== null) {
            return errorBody.error ?? errorBody.message ?? errorBody.detail ?? JSON.stringify(errorBody);
        }
        return `[${response.status}]: an unknown error occured :'(`;
    } catch (e) {
        return `[${response.status}]: an unknown error occured :'(`;
    }
}

export async function downloadBlob(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
    a.click();
    a.remove();  //afterwards we remove the element again
}